import { authenticatedInstance } from '../index/index.service';
import { defaultStreamUrl, subscribeStream } from '../sse-subscribe/sse-subscribe.util';
import type {
  ApprovalRequestView,
  AttendanceReportRules,
  AreaTableView,
  AreaViewBase,
  AreaWithTablesView,
  CategoryView,
  CreditTransactionView,
  EmployeeEventView,
  FloorBackground,
  HappyHourView,
  OrderView,
  OrderlineView,
  PaymentTerminalView,
  PaymentlineView,
  PosStreamEvent as SharedPosStreamEvent,
  PosRefreshReason,
  PrinterView,
  ProductView,
  SessionView,
  VariantComboGroupView,
} from 'shared-models/browser';
import type { ApprovalActionType } from 'shared-models/browser';

export type { PosRefreshReason };

// Summary: Single-channel multiplexed POS event union with a strict accepted-type allow-list.
// Details: docs/function-library/web/services.md#pos-stream-service
export type PosStreamEvent =
  | { type: 'order.opened'; order: OrderView }
  | { type: 'order.upserted'; order: OrderView }
  | { type: 'order.closed'; orderId: number }
  | {
      type: 'orderline.added';
      orderId: number;
      orderline: OrderlineView;
      actorEmployeeId?: number;
      actorEmployeeName?: string;
      addedAmount?: number;
    }
  | {
      type: 'orderline.updated';
      orderId: number;
      orderline: OrderlineView;
      previousQuantity: number;
      previousUnitPrice?: number;
      actorEmployeeId?: number;
      actorEmployeeName?: string;
      addedAmount?: number;
    }
  | { type: 'orderline.item_payment_updated'; orderId: number; items: Array<{ orderlineId: number; paidQuantity: number }> }
  | { type: 'orderline.removed'; orderId: number; orderlineId: number; lineTotal: number }
  | {
      type: 'orderline.sent';
      orderId: number;
      sentLineIds: number[];
      skippedLineIds: number[];
      skippedLineCount?: number;
      actorEmployeeId?: number;
      actorEmployeeName?: string;
      sentAmount?: number;
    }
  | { type: 'paymentline.created'; orderId: number; paymentline: PaymentlineView }
  | { type: 'paymentline.done'; orderId: number; paymentlineId: number }
  | { type: 'credit.tap.pending'; orderId: number; paymentlineId: number }
  | { type: 'credit.tap.released'; orderId: number; paymentlineId: number }
  | { type: 'credit.transaction.pending'; orderId: number; paymentlineId: number }
  | {
      type: 'credit.transaction.resolved';
      orderId: number;
      paymentlineId: number;
      paymentlineStatus: 'new' | 'done' | 'canceled_split' | 'canceled_reversed';
      orderClosed: boolean;
      transaction: CreditTransactionView;
    }
  | { type: 'session.opened'; session: SessionView }
  | { type: 'session.closed'; sessionId: number; autoClosed: boolean }
  | { type: 'cashDrawer.notConfigured' }
  | { type: 'session.daily_brief_updated'; sessionId: number; dailyBrief: string | null; updatedByName: string }
  | { type: 'session.auto_close_time_updated'; tenantId: string; autoCloseTime: string | null }
  | { type: 'tenant.floor_background_updated'; tenantId: string; floorBackground: FloorBackground }
  | {
      type: 'tenant.general_item_requires_manager_approval_updated';
      tenantId: string;
      generalItemRequiresManagerApproval: boolean;
    }
  | {
      type: 'tenant.action_requires_manager_approval_updated';
      tenantId: string;
      actionType: Exclude<ApprovalActionType, 'general_item'>;
      required: boolean;
    }
  | {
      type: 'tenant.operations_approval_requirement_updated';
      tenantId: string;
      actionType: 'transfer' | 'merge' | 'split_merged_table';
      required: boolean;
    }
  | {
      type: 'tenant.orders_page_waiter_own_visibility_updated';
      tenantId: string;
      ordersPageWaiterOwnVisibility: boolean;
    }
  | {
      type: 'tenant.kitchen_bon_table_number_at_bottom_updated';
      tenantId: string;
      kitchenBonTableNumberAtBottom: boolean;
    }
  | {
      type: 'tenant.stay_in_order_after_fire_updated';
      tenantId: string;
      stayInOrderAfterFire: boolean;
    }
  | {
      type: 'tenant.employee_idle_logout_minutes_updated';
      tenantId: string;
      employeeIdleLogoutMinutes: number;
    }
  | {
      type: 'tenant.warn_no_bill_before_payment_updated';
      tenantId: string;
      warnNoBillBeforePayment: boolean;
    }
  | {
      type: 'tenant.hide_general_item_in_menu_updated';
      tenantId: string;
      hideGeneralItemInMenu: boolean;
    }
  | {
      type: 'tenant.disable_cash_tips_updated';
      tenantId: string;
      disableCashTips: boolean;
    }
  | {
      type: 'tenant.waiter_own_order_entry_only_updated';
      tenantId: string;
      waiterOwnOrderEntryOnly: boolean;
    }
  | {
      type: 'tenant.single_worker_per_table_updated';
      tenantId: string;
      singleWorkerPerTable: boolean;
    }
  | {
      type: 'tenant.manual_credit_entry_blocked_updated';
      tenantId: string;
      manualCreditEntryBlocked: boolean;
    }
  | {
      type: 'tenant.customer_name_on_order_entry_updated';
      tenantId: string;
      customerNameOnOrderEntry: boolean;
    }
  | {
      type: 'tenant.large_table_number_on_receipt_updated';
      tenantId: string;
      largeTableNumberOnReceipt: boolean;
    }
  | {
      type: 'tenant.station_waiter_selection_enabled_updated';
      tenantId: string;
      stationWaiterSelectionEnabled: boolean;
    }
  | {
      type: 'tenant.stationary_name_updated';
      tenantId: string;
      stationaryName: string | null;
    }
  | {
      type: 'tenant.cash_close_manager_only_updated';
      tenantId: string;
      cashCloseManagerOnly: boolean;
    }
  | Extract<SharedPosStreamEvent, { type: 'tenant.exclude_opening_cash_from_drawer_updated' }>
  | {
      type: 'tenant.open_drawer_on_partial_cash_updated';
      tenantId: string;
      openDrawerOnPartialCash: boolean;
    }
  | {
      type: 'tenant.open_drawer_employee_ids_updated';
      tenantId: string;
      openDrawerEmployeeIds: number[];
    }
  | {
      type: 'tenant.open_drawer_requires_password_updated';
      tenantId: string;
      openDrawerRequiresPassword: boolean;
    }
  | {
      type: 'tenant.employee_shift_close_enabled_updated';
      tenantId: string;
      employeeShiftCloseEnabled: boolean;
    }
  | {
      type: 'tenant.area_print_rules_updated';
      tenantId: string;
      areaPrintRules: import('shared-models/browser').AreaPrintRule[];
    }
  | Extract<SharedPosStreamEvent, { type: 'tenant.refund_code_via_push_updated' }>
  | Extract<SharedPosStreamEvent, { type: 'tenant.z_report_email_recipients_updated' }>
  | {
      type: 'tenant.employee_attendance_report_enabled_updated';
      tenantId: string;
      employeeAttendanceReportEnabled: boolean;
    }
  | {
      type: 'tenant.attendance_report_rules_updated';
      tenantId: string;
      attendanceReportRules: AttendanceReportRules;
    }
  | { type: 'session.cash_alterations_updated'; sessionId: number; cashAlterations: number }
  | { type: 'area.upserted'; area: AreaViewBase | AreaWithTablesView }
  | { type: 'table.upserted'; areaId: number; table: AreaTableView }
  | { type: 'table.removed'; areaId: number; tableId: number }
  | { type: 'employee.upserted'; employee: EmployeeEventView }
  | { type: 'employee.deactivated'; employeeId: number }
  | { type: 'employee.kicked'; employeeId: number; kickedSessionId: string }
  | { type: 'product.upserted'; product: ProductView }
  | { type: 'product.deactivated'; productId: number }
  | { type: 'payment-terminal.upserted'; terminal: PaymentTerminalView }
  | { type: 'payment-terminal.active-changed'; terminalId: number; isActive: boolean }
  | { type: 'category.upserted'; category: CategoryView }
  | { type: 'printer.upserted'; printer: PrinterView }
  | { type: 'happy_hour.changed'; happyHour: HappyHourView }
  | { type: 'variantComboGroup.upserted'; productId: number; group: VariantComboGroupView }
  | { type: 'variantComboGroup.removed'; productId: number; groupId: number }
  | { type: 'merge.links.changed' }
  | { type: 'approval_request.created'; orderId: number; request: ApprovalRequestView }
  | { type: 'approval_request.decided'; orderId: number; request: ApprovalRequestView; autoApprovedEmployeeGeneralItem?: boolean }
  | { type: 'tenant.maintenance-mode-changed'; maintenanceMode: boolean }
  | { type: 'pos.refresh-requested'; sessionId: number | null; reason?: PosRefreshReason }
  | Extract<SharedPosStreamEvent, { type: 'deployment.refresh-requested' }>
  | { type: 'employee.presence.probe.requested'; probeId: string; targetSessionId: string }
  | Extract<SharedPosStreamEvent, { type: 'green_fire.rescue.requested' }>;

export const POS_STREAM_EVENT_TYPES: ReadonlyArray<PosStreamEvent['type']> = [
  'order.opened',
  'order.closed',
  'order.upserted',
  'orderline.added',
  'orderline.updated',
  'orderline.item_payment_updated',
  'orderline.removed',
  'orderline.sent',
  'paymentline.created',
  'paymentline.done',
  'credit.tap.pending',
  'credit.tap.released',
  'credit.transaction.pending',
  'credit.transaction.resolved',
  'session.opened',
  'session.closed',
  'cashDrawer.notConfigured',
  'session.daily_brief_updated',
  'session.auto_close_time_updated',
  'tenant.floor_background_updated',
  'tenant.general_item_requires_manager_approval_updated',
  'tenant.action_requires_manager_approval_updated',
  'tenant.operations_approval_requirement_updated',
  'tenant.orders_page_waiter_own_visibility_updated',
  'tenant.kitchen_bon_table_number_at_bottom_updated',
  'tenant.warn_no_bill_before_payment_updated',
  'tenant.waiter_own_order_entry_only_updated',
  'tenant.single_worker_per_table_updated',
  'tenant.manual_credit_entry_blocked_updated',
  'tenant.customer_name_on_order_entry_updated',
  'tenant.station_waiter_selection_enabled_updated',
  'tenant.stationary_name_updated',
  'tenant.employee_shift_close_enabled_updated',
  'tenant.area_print_rules_updated',
  'tenant.cash_close_manager_only_updated',
  'tenant.exclude_opening_cash_from_drawer_updated',
  'tenant.open_drawer_on_partial_cash_updated',
  'tenant.open_drawer_employee_ids_updated',
  'tenant.open_drawer_requires_password_updated',
  'tenant.refund_code_via_push_updated',
  'tenant.employee_attendance_report_enabled_updated',
  'tenant.attendance_report_rules_updated',
  'session.cash_alterations_updated',
  'area.upserted',
  'table.upserted',
  'table.removed',
  'employee.upserted',
  'employee.deactivated',
  'employee.kicked',
  'product.upserted',
  'product.deactivated',
  'payment-terminal.upserted',
  'payment-terminal.active-changed',
  'category.upserted',
  'printer.upserted',
  'happy_hour.changed',
  'variantComboGroup.upserted',
  'variantComboGroup.removed',
  'merge.links.changed',
  'approval_request.created',
  'approval_request.decided',
  'tenant.maintenance-mode-changed',
  'pos.refresh-requested',
  'deployment.refresh-requested',
  'employee.presence.probe.requested',
  'green_fire.rescue.requested',
];
// Summary: Mints a single-use HMAC-signed SSE ticket bound to tenant+role for the multiplexed POS stream so the JWT employee-session token...
// Details: docs/function-library/web/services.md#pos-stream-service
async function mintPosStreamTicket(tenantId: string, employeeSession: string | null): Promise<string> {
  const headers = employeeSession ? { 'x-employee-session': employeeSession } : undefined;
  const response = await authenticatedInstance.post<{ ticket: string; expiresAt: number }>(
    `/pos/tenant/${encodeURIComponent(tenantId)}/stream-ticket`,
    {},
    { headers },
  );
  if (!response.data?.ticket) {
    throw new Error('POS stream ticket missing in response');
  }
  return response.data.ticket;
}

// Summary: Manager-only POST asking the backend to fan out a refresh request to all tenant devices.
export async function broadcastPosRefresh(tenantId: string, employeeSession: string): Promise<void> {
  await authenticatedInstance.post(
    `/pos/tenant/${encodeURIComponent(tenantId)}/refresh-broadcast`,
    {},
    { headers: { 'x-employee-session': employeeSession } },
  );
}

// Summary: Strict per-event payload validation. The type allowlist alone does not guarantee the
export function hasValidEventPayload(event: PosStreamEvent): boolean {
  switch (event.type) {
    case 'table.upserted':
      return (
        typeof event.areaId === 'number' &&
        Number.isFinite(event.areaId) &&
        !!event.table &&
        typeof event.table === 'object' &&
        typeof (event.table as { id?: unknown }).id === 'number'
      );
    case 'table.removed':
      return (
        typeof event.areaId === 'number' &&
        Number.isFinite(event.areaId) &&
        typeof event.tableId === 'number' &&
        Number.isFinite(event.tableId)
      );
    case 'tenant.maintenance-mode-changed':
      return typeof event.maintenanceMode === 'boolean';
    case 'tenant.waiter_own_order_entry_only_updated':
      return typeof event.waiterOwnOrderEntryOnly === 'boolean';
    case 'tenant.single_worker_per_table_updated':
      return typeof event.singleWorkerPerTable === 'boolean';
    case 'tenant.open_drawer_on_partial_cash_updated':
      return typeof event.openDrawerOnPartialCash === 'boolean';
    case 'tenant.open_drawer_employee_ids_updated':
      return Array.isArray(event.openDrawerEmployeeIds) && event.openDrawerEmployeeIds.every((id) => Number.isInteger(id) && id > 0);
    case 'tenant.open_drawer_requires_password_updated':
      return typeof event.openDrawerRequiresPassword === 'boolean';
    default:
      return true;
  }
}

// Summary: Opens the multiplexed per-tenant POS SSE channel for POS, areas, employees, and products events.
// Details: docs/function-library/web/services.md#pos-stream-service
export function subscribePosStream(
  tenantId: string,
  employeeSession: string | null,
  onEvent: (event: PosStreamEvent) => void,
  onEventError?: (error: unknown) => void,
  onError?: () => void,
  onResync?: () => void,
  onRecovery?: (inactiveMs?: number) => void,
  onOpen?: () => void,
): () => void {
  return subscribeStream<PosStreamEvent>({
    mintTicket: () => mintPosStreamTicket(tenantId, employeeSession),
    buildStreamUrl: (ticket, lastEventId) =>
      defaultStreamUrl(`/pos/tenant/${encodeURIComponent(tenantId)}/stream`, ticket, lastEventId),
    parseEvent: (payload) => {
      const candidate = payload as PosStreamEvent | undefined;
      if (
        candidate &&
        typeof candidate.type === 'string' &&
        POS_STREAM_EVENT_TYPES.includes(candidate.type) &&
        hasValidEventPayload(candidate)
      ) {
        return candidate;
      }
      return null;
    },
    onEvent,
    onEventError,
    onError,
    onOpen,
    onResync,
    onRecovery,
  });
}

